// src/com/auto_using_car_info/AutoUsingCarInfo.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import EditNoteIcon from '@mui/icons-material/EditNote';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CheckIcon from '@mui/icons-material/Check'; // selected dc
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RemoveIcon from '@mui/icons-material/Remove'; //no com list
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'; // closed com list
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'; // opened com list
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import {
    authTableTreeRepo, autoUsingCarInfoListRepo, autoUsingCarInfoCacheRepo, autoUsingCarInfoParamRepo,
    comInfoListRepo, userInfoRepo
} from '../../value/AppModel';
import { LIST_COM_INFO, makeComInfoTree } from '../com_info/ComInfoGql';
import {
    LIST_AUTO_USING_CAR_INFO,
    UPLOAD_AUTO_USING_CAR_INFO, REMOVE_AUTO_USING_CAR_INFO
} from './AutoUsingCarInfoGql';
import { AutoUsingCarInfoDictionary } from './AutoUsingCarInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import AutoUsingCarInfoInput from './AutoUsingCarInfoInput';
import ValueMisc from '../../value/ValueMisc';
import { NonCapButton } from '../../common/VzmStyled';

const ColumnContainer = styled(Box)({
    display: 'flex', flexDirection: 'column', boxSizing: 'border-box', // height: '100%', border: '1px solid red'
});

const DcTypeLine = styled('div')({
    display: 'flex', alignItems: 'center', backgroundColor: '#eee'
});
const DcTypeLabel = styled('span')({
    fontSize: '0.9rem', color: AppStyle.PrimaryRGB, width: 100, display: 'inline-block',
    padding: 5, fontWeight:'bold' // border: AppStyle.BorderCCC, borderRadius: 5, textAlign: 'center',
});

const DcGroup = styled('div')({
    marginLeft: 32, fontSize: '0.85rem',
});
const DcLabelLine = styled('div')({
    display: 'flex', alignItems: 'center',
});

const ComListBox = styled('div')({
    marginLeft: 40
});

const ComName = styled('div')({padding:2, color:'#777', fontSize:'0.8rem'});

/**
 * 가용차량 등록
 * (AUTO_USING_CAR_INFO) 정보 View Controller
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-14 16:29:42 using DbdbTap
 */
export default function AutoUsingCarInfo({
    viewServiceTags, // for switching view
    show = true,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [dcToOpen, setDcToOpen] = useState(null); // dc_cd to see com_info list in the tree.
    const autoUsingCarInfoParam = useReactiveVar(autoUsingCarInfoParamRepo);
    const autoUsingCarInfoListData = useReactiveVar(autoUsingCarInfoListRepo);
    const autoUsingCarInfoCache = useReactiveVar(autoUsingCarInfoCacheRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [comInfoTree, setComInfoTree] = useState([]); // from comInfoListData. dcTypeCd => dcCd => comId
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const openRef = useRef(false);
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });

    const [getAutoUsingCarInfoList, responseList] = useLazyQuery(LIST_AUTO_USING_CAR_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: AutoUsingCarInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [uploadAutoUsingCarInfo, responseUpload] = useMutation( UPLOAD_AUTO_USING_CAR_INFO, {
		onCompleted: (data, option) => onCompleteUpload(data, option), 
		onError: (error) => addAlert({title: AutoUsingCarInfoDictionary.errorTitle.Upload, message: [error.message]})
	} );

    const [removeAutoUsingCarInfo, responseRemove] = useMutation( REMOVE_AUTO_USING_CAR_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: AutoUsingCarInfoDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getAutoUsingCarInfoListNow = (dcCd, ymd) => {
        const param = {
            variables:{
                dcCd:dcCd || autoUsingCarInfoParam.dcCd,
                planDate:ymd || autoUsingCarInfoParam.planDate
            }
        };
        getAutoUsingCarInfoList(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        if(!openRef.current) {
            openRef.current = true;
            const ymd = autoUsingCarInfoParam.planDate || ValueMisc.getYMD(new Date());
            const dcCd = autoUsingCarInfoParam.dcCd || sessionInfo.user.getDcCd();
            if(ymd !== autoUsingCarInfoParam.planDate || dcCd !== autoUsingCarInfoParam.dcCd) {
                autoUsingCarInfoParamRepo({dcCd:dcCd, planDate:ymd});
                getAutoUsingCarInfoListNow(dcCd, ymd);
            }

            const diff = new Date().getTime()/1000 - comInfoListData.when;
            if(diff > 300) getComInfoList();
        }

    }, []);

    useEffect(()=>{
        if(comInfoListData.records.length > 0) {
            const tree = makeComInfoTree(comInfoListData.records);
            setComInfoTree(tree);
        }
    }, [comInfoListData]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetComList = (data, option) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.autoUsingCarInfoList) {
            autoUsingCarInfoListRepo({when:new Date().getTime()/1000, records:data.autoUsingCarInfoList});
            if(data.autoUsingCarInfoList.length === 0) {
                enqueueSnackbar('해당 센터와 날짜에 대한 가용차량 설정 정보가 없습니다.',
                    {variant:'info', autoHideDuration:2000});
            }
            else {
                const cacheBackup = {...autoUsingCarInfoCache};
                const dcCd = autoUsingCarInfoParam.dcCd;
                const planDate = autoUsingCarInfoParam.planDate;

                // save to cache if the date is later than cached date.
                if(cacheBackup[dcCd] === undefined) {
                    cacheBackup[dcCd] = {
                        date: '2000-01-01',
                        cache: {}
                    };
                }
                if(cacheBackup[dcCd].date < planDate) {
                    const cache = {};
                    data.autoUsingCarInfoList.forEach((item)=>{
                        cache[item.vehTypeId] = item.usingCarCnt;
                    });
                    cacheBackup[dcCd] = {
                        date: planDate,
                        cache: cache
                    }
                    autoUsingCarInfoCacheRepo(cacheBackup);
                }
            }
        }
    };

    const onCompleteUpload = (data, clientOption) => {
        if(data.autoUsingCarInfoUpload.ok) {
            // check if any item is not saved.
            const errors = data.autoUsingCarInfoUpload.list
                .filter((item)=>item.ok!==true)
                .map((item)=>{
                    return `Code ${item.vehTypeId}: ${item.message}`;
                });
            setEditorState(AppObject.EmptyViewerState);
            if(errors.length > 0)
                addAlert({
                    title:'일부 차종의 가용차량 수 설정에 실패했습니다.',
                    message:errors
                });
            getAutoUsingCarInfoListNow();
        }
        else addAlert({title:AutoUsingCarInfoDictionary.errorTitle.Upload, message:[data.autoUsingCarInfoUpload.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.autoUsingCarInfoRemove.ok) getAutoUsingCarInfoListNow();
        else addAlert({title:AutoUsingCarInfoDictionary.errorTitle.Remove, message:[data.autoUsingCarInfoRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<
    const onChangePlanDate = (date)=>{
        // setDatePicked(date);
        const ymd = ValueMisc.getYMD(date.toDate());
        autoUsingCarInfoParamRepo({dcCd:autoUsingCarInfoParam.dcCd, planDate:ymd});
        getAutoUsingCarInfoListNow(autoUsingCarInfoParam.dcCd, ymd);
    };

    const onClickDc = (dc) => {
        autoUsingCarInfoParamRepo({dcCd:dc.dcCd, planDate:autoUsingCarInfoParam.planDate});
        getAutoUsingCarInfoListNow(dc.dcCd, autoUsingCarInfoParam.planDate);
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getAutoUsingCarInfoListNow();
    };

    const onRequestSave = (list) => {
        uploadAutoUsingCarInfo({variables:{autoUsingCarInfo:list}});
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "가용차량 설정 정보 삭제",
            content: [
                `차종 ${item.vehTypeName}에 대한 설정을 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeAutoUsingCarInfo({variables:{autoUsingCarInfo:{dcCd:item.dcCd, planDate:item.planDate, vehTypeId:item.vehTypeId}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            default: return;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_auto_using_car_info_save);
    const canRemove = checkAuth(authTableTree.tree.pr_auto_using_car_info_remove);
    /* const tools = canRemove ? [
        {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
    ] : []; */
    const tools = [];
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const renderComListBox = (dc) => {
        return(
            <ComListBox>
                {
                    dc.comList
                    .sort((a,b)=>a.comName.localeCompare(b.comName))
                    .map((com) => {
                        return <ComName key={com.comId}>- {com.comName}</ComName>
                    })
                }
            </ComListBox>
        );
    }

    const renderComTree = () => {
        return(
            <Box
                style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    overflow: 'auto', 
                }}
            >
                {
                    comInfoTree
                    .map((dcType, idx) => {
                        return (
                            <Box key={dcType.dcTypeCd}>
                                <DcTypeLine>
                                    <DcTypeLabel>{dcType.dcTypeName}</DcTypeLabel>
                                </DcTypeLine>
                                <div>
                                    {
                                        dcType.dcList
                                        .sort((a,b)=>a.dcName.localeCompare(b.dcName))
                                        .map((dc) => {
                                            return (
                                                <DcGroup key={dc.dcCd}>
                                                    <DcLabelLine>
                                                        {
                                                            dc.comList.length === 0 ?
                                                            <IconButton
                                                                disabled
                                                                size={AppWord.SMALL}
                                                            >
                                                                <RemoveIcon style={{fontSize:12, color:'#777'}} />
                                                            </IconButton>
                                                            :
                                                            dcToOpen === dc.dcCd ?
                                                            <IconButton
                                                                size={AppWord.SMALL}
                                                                onClick={()=>setDcToOpen(null)}
                                                            >
                                                                <KeyboardArrowDownIcon style={{fontSize:12, color:'#777'}} />
                                                            </IconButton>
                                                            :
                                                            <IconButton
                                                                size={AppWord.SMALL}
                                                                onClick={()=>setDcToOpen(dc.dcCd)}
                                                            >
                                                                <KeyboardArrowRightIcon style={{fontSize:12, color:'#777'}} />
                                                            </IconButton>
                                                        }
                                                        <NonCapButton
                                                            size={AppWord.SMALL}
                                                            startIcon={
                                                                autoUsingCarInfoParam.dcCd === dc.dcCd ?
                                                                <CheckIcon style={{fontSize:12, color:AppStyle.PrimaryRGB}} />
                                                                : <HomeWorkIcon style={{fontSize:12, color:'#777'}} />
                                                            }
                                                            onClick={()=>onClickDc(dc)}
                                                            disabled={autoUsingCarInfoParam.dcCd === dc.dcCd || responseList.loading}
                                                        >
                                                            {dc.dcName}
                                                        </NonCapButton>
                                                    </DcLabelLine>
                                                    {dc.dcCd === dcToOpen && renderComListBox(dc)}
                                                </DcGroup>
                                            )
                                        })
                                    }
                                </div>
                            </Box>
                        )
                    })
                }
            </Box>
        );
    };

    const loadingAny
        = responseList.loading
        || responseComList.loading
        || responseUpload.loading
        || responseRemove.loading;

    const rootStyle = show ? undefined : {display:'none', visibility:'hidden', pointerEvents:'none'};

    return(
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <ContentConainer style={rootStyle}>
                {viewServiceTags}
                <ContentBody style={{display:'flex'}}>
                    <ColumnContainer width={280} style={{paddingRight:10}}>
                        <ContentHeader>
                            <DatePicker label={'배차 날짜'}
                                sx={{width:150}}
                                slotProps={{
                                    openPickerIcon:{fontSize:AppWord.SMALL},
                                    textField:{size:AppWord.SMALL}
                                }}
                                format='YYYY-MM-DD'
                                value={autoUsingCarInfoParam.planDate ? dayjs(autoUsingCarInfoParam.planDate) : ''}
                                onChange={onChangePlanDate}
                                disabled={responseList.loading}
                            />
                        </ContentHeader>
                        <Box style={{
                            flexGrow:1, position:'relative',
                            border: AppStyle.BorderCCC, boxSizing:'border-box',
                            borderRadius:5, padding:10,
                        }}
                        >
                            {renderComTree()}
                        </Box>
                    </ColumnContainer>
                    <ColumnContainer flexGrow={1}>
                        <ContentHeader>
                            <ControlBox>
                                <SearchBox
                                    value={searchText}
                                    onChangeText={onChangeSearchText}
                                />
                            </ControlBox>
                            <ToolBox>
                                {
                                    loadingAny ?
                                    <CircularProgress size={20} />
                                    :
                                    <IconButton
                                        color={AppStyle.mui.inherit}
                                        onClick={onClickReload}
                                    >
                                        <CachedIcon />
                                    </IconButton>
                                }
                                {
                                    canAdd &&
                                    <IconButton
                                        color={AppStyle.mui.primary}
                                        style={{marginLeft:10}}
                                        onClick={onClickAdd}
                                        disabled={
                                            loadingAny
                                            || !autoUsingCarInfoParam.dcCd
                                            || !autoUsingCarInfoParam.planDate
                                        }
                                    >
                                        <EditNoteIcon />
                                    </IconButton>
                                }
                            </ToolBox>
                        </ContentHeader>
                        <FlexyTable
                            name={AutoUsingCarInfoDictionary.tableProps.tableId}
                            tableProps={AutoUsingCarInfoDictionary.tableProps}
                            records={autoUsingCarInfoListData.records}
                            search={searchText}
                            tools={tools}
                            onClickTool={onClickToolOnRow}
                        />
                    </ColumnContainer>
                </ContentBody>

                <AutoUsingCarInfoInput
                    open={editorState.open}
                    planDate={autoUsingCarInfoParam.planDate}
                    dcCd={autoUsingCarInfoParam.dcCd}
                    onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                    responseSaving={responseUpload}
                    onRequestSave={onRequestSave}
                />
            </ContentConainer>
        </LocalizationProvider>
    );
}
